import { useMemo, useState } from "react"
import { Info, ChevronDown, ArrowRight, TriangleAlert, Mountain, Droplets, Factory } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Skeleton } from "@/components/ui/skeleton"
import { computePotentiel } from "@/lib/potentiel-utils"
import { useGeorisques, type GeorisquesData } from "@/hooks/use-georisques"
import type { PotentielData, PotentielSourceCitation, PotentielField } from "@/types"

const confidenceColor = (c: string | null | undefined) => {
  if (c === "high" || c === "certain") return "bg-emerald-500"
  if (c === "medium" || c === "uncertain") return "bg-amber-500"
  return "bg-gray-400"
}

function SourceTooltip({ sources }: { sources: PotentielSourceCitation[] }) {
  if (sources.length === 0) return null
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex items-center text-muted-foreground hover:text-foreground">
            <Info className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-2 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Sources PLU</p>
          {sources.map((s, i) => (
            <div key={i} className="space-y-0.5">
              {i > 0 && <div className="border-t border-border" />}
              <div className="flex items-center gap-1.5">
                <span className="inline-flex h-5 items-center rounded bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">
                  p.{s.page}
                </span>
                {s.pdfUrl && (
                  <a
                    href={s.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-[10px] text-primary hover:underline"
                  >
                    PDF
                  </a>
                )}
              </div>
              <p className="text-[11px] italic text-muted-foreground leading-snug">
                &laquo;&nbsp;{s.extrait}&nbsp;&raquo;
              </p>
            </div>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function BuildingSection({ buildings }: { buildings: PotentielData["buildings"] }) {
  const [open, setOpen] = useState(false)
  if (!buildings || buildings.length === 0) return null

  const buildingRows = buildings.map((building) =>
    [
      { label: "Hauteur moyenne", value: building.hauteur_mean ? `${building.hauteur_mean} m` : null },
      { label: "Niveaux", value: building.nb_niveau ? `${building.nb_niveau}` : null },
      {
        label: "Emprise au sol",
        value: building.surface_emprise_sol
          ? `${Math.round(building.surface_emprise_sol).toLocaleString("fr-FR")} m²`
          : null,
      },
      { label: "Annee de construction", value: building.annee_construction },
      { label: "Usage", value: building.usage_niveau_1_txt },
      { label: "Materiaux murs", value: building.mat_mur_txt },
      { label: "Materiaux toiture", value: building.mat_toit_txt },
      { label: "Nombre de logements", value: building.nb_log && building.nb_log > 0 ? `${building.nb_log}` : null },
    ].filter((r) => r.value != null)
  ).filter((rows) => rows.length > 0)

  if (buildingRows.length === 0) return null

  return (
    <div className="border-t border-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50 cursor-pointer"
      >
        <span className="font-medium">
          {buildingRows.length === 1 ? "Batiment actuel (BDNB)" : `${buildingRows.length} batiments actuels (BDNB)`}
        </span>
        <ChevronDown className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-1 px-3 pb-3">
          {buildingRows.map((rows, bIdx) => (
            <div key={bIdx}>
              {buildingRows.length > 1 && (
                <p className={`text-[11px] font-semibold text-muted-foreground ${bIdx > 0 ? "mt-2 pt-2 border-t border-border" : ""}`}>
                  Batiment {bIdx + 1}
                </p>
              )}
              {rows.map((r) => (
                <div key={r.label} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{r.label}</span>
                  <span className="font-medium">{r.value}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function PluSection({ pluZone }: { pluZone: PotentielData["plu_zone"] }) {
  const [open, setOpen] = useState(false)
  if (!pluZone) return null

  const confidenceLabel = (c: string | null | undefined) => {
    if (c === "high" || c === "certain") return "Fiable"
    if (c === "medium" || c === "uncertain") return "Incertain"
    return null
  }

  const renderField = (label: string, field: PotentielField<number>, unit: string) => (
    <div className="rounded border border-border bg-muted/30 p-2.5 space-y-1">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] text-muted-foreground">{label}</p>
          <p className="font-semibold text-sm">{field.value != null ? `${field.value} ${unit}` : "—"}</p>
        </div>
        {confidenceLabel(field.confidence) && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
              field.confidence === "high" || field.confidence === "certain"
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            {confidenceLabel(field.confidence)}
          </span>
        )}
      </div>
      {field.sources && field.sources.length > 0 && (
        <div className="border-t border-border pt-1.5 space-y-1">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Sources</p>
          {field.sources.map((s, i) => (
            <div key={i} className="flex items-baseline gap-1.5">
              <span className="inline-flex h-4 items-center rounded bg-primary/10 px-1 text-[10px] font-semibold text-primary shrink-0">
                p.{s.page}
              </span>
              <p className="text-[11px] italic text-muted-foreground leading-snug flex-1">
                &laquo;&nbsp;{s.extrait}&nbsp;&raquo;
              </p>
              {s.pdfUrl && (
                <a
                  href={s.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-primary hover:underline shrink-0"
                >
                  PDF
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="border-t border-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50 cursor-pointer"
      >
        <span className="font-medium">Donnees PLU</span>
        <ChevronDown className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-2 px-3 pb-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Zone</span>
            <span className="font-medium">{pluZone.libelle || "—"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Type de zone</span>
            <span className="font-medium">{pluZone.zoneType || "—"}</span>
          </div>
          {renderField("Emprise au sol", pluZone.empriseAuSol, "%")}
          {renderField("Hauteur max", pluZone.hauteurMax, "m")}
          {renderField("Etages max", pluZone.etageMax, "")}
          {pluZone.notes && (
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">Notes</p>
              <p className="text-sm">{pluZone.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const HIGHLIGHTED_RISKS = [
  { natKey: "retraitGonflementArgile", label: "Argile", icon: Mountain, textColor: "text-amber-700", bgColor: "bg-amber-50" },
  { natKey: "inondation", label: "Inondation", icon: Droplets, textColor: "text-blue-700", bgColor: "bg-blue-50" },
  { techKey: "pollutionSols", label: "Pollution", icon: Factory, textColor: "text-rose-700", bgColor: "bg-rose-50" },
] as const

function RisquesSection({ data, loading }: { data: GeorisquesData | null; loading: boolean }) {
  const [open, setOpen] = useState(false)

  if (!loading && !data) return null

  const natEntries = data ? Object.entries(data.risquesNaturels) : []
  const techEntries = data ? Object.entries(data.risquesTechnologiques) : []
  const totalPresent = natEntries.filter(([, r]) => r.present).length + techEntries.filter(([, r]) => r.present).length

  const highlightedPresent = data
    ? HIGHLIGHTED_RISKS.filter((h) => {
        if ("natKey" in h && h.natKey) return data.risquesNaturels[h.natKey]?.present
        if ("techKey" in h && h.techKey) return data.risquesTechnologiques[h.techKey]?.present
        return false
      })
    : []

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-muted-foreground hover:bg-muted/50 cursor-pointer"
      >
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="text-base font-semibold text-foreground">Risques</span>
          {!loading && data && (
            <>
              <span className="text-xs text-muted-foreground">
                {totalPresent > 0 ? `${totalPresent} risque${totalPresent > 1 ? "s" : ""}` : "Aucun"}
              </span>
              {highlightedPresent.map((h) => {
                const Icon = h.icon
                return (
                  <span
                    key={h.label}
                    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${h.bgColor} ${h.textColor}`}
                  >
                    <Icon className="size-3" />
                    {h.label}
                  </span>
                )
              })}
            </>
          )}
        </div>
        <ChevronDown className={`size-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/5" />
            </div>
          ) : data ? (
            <div className="space-y-3">
              {natEntries.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                    <span className="size-2 rounded-full bg-red-500" />
                    Risques naturels
                  </div>
                  <div className="space-y-1">
                    {natEntries.map(([key, detail]) => (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{detail.libelle || key}</span>
                        <span className={detail.present ? "text-red-600 font-medium" : "text-muted-foreground/60"}>
                          {detail.present ? "Présent" : "Absent"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {techEntries.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                    <span className="size-2 rounded-full bg-amber-500" />
                    Risques technologiques
                  </div>
                  <div className="space-y-1">
                    {techEntries.map(([key, detail]) => (
                      <div key={key} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{detail.libelle || key}</span>
                        <span className={detail.present ? "text-red-600 font-medium" : "text-muted-foreground/60"}>
                          {detail.present ? "Présent" : "Absent"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.url && (
                <a
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  Voir le rapport Géorisques
                  <svg className="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export function PotentielTab({ data, latitude, longitude }: { data: PotentielData | null; latitude: number | null; longitude: number | null }) {
  const { data: risquesData, loading: risquesLoading } = useGeorisques(latitude, longitude)
  const computed = useMemo(() => (data ? computePotentiel(data) : null), [data])
  const rows = computed?.rows ?? []
  // SDP delta summary chip
  const sdpSummary = useMemo(() => {
    const sdpRow = rows.find((r) => r.label === "SDP")
    if (!sdpRow || sdpRow.diff === "—") return null
    const match = sdpRow.diff.match(/\(([^)]+)\)/)
    if (!match) return null
    return { label: `SDP ${match[1]}`, isPositive: sdpRow.isPositive }
  }, [rows])

  if (!data) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        Aucune donnee disponible — ajoutez des coordonnees au projet.
      </p>
    )
  }

  const { parcel, buildings, plu_zone } = data

  if (!parcel && buildings.length === 0 && !plu_zone) {
    return <p className="py-4 text-sm text-muted-foreground">Aucune parcelle trouvee aux coordonnees du projet.</p>
  }

  return (
    <div className="space-y-3 py-2">
      {/* Header with SDP summary */}
      {sdpSummary && (
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              sdpSummary.isPositive
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20"
                : "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20"
            }`}
          >
            {sdpSummary.label}
          </span>
        </div>
      )}

      {/* Parcel info */}
      {parcel && (
        <div className="flex gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Parcelle </span>
            <span className="font-medium">{parcel.idu}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Surface </span>
            <span className="font-medium">{parcel.contenance.toLocaleString("fr-FR")} m²</span>
          </div>
        </div>
      )}

      {/* Risques */}
      <div className="rounded-lg border border-border overflow-hidden">
        <RisquesSection data={risquesData} loading={risquesLoading} />
      </div>

      {/* Metric rows */}
      <div className="rounded-lg border border-border overflow-hidden">
        {rows.map((row, idx) => {
          const hasEstimation =
            (row.label === "Etages" || row.label === "SDP" || row.label === "SHAB") &&
            (computed?.floorsCurrentEstimated || computed?.floorsPluEstimated)

          return (
            <div key={row.label} className={`px-3 py-2.5 ${idx > 0 ? "border-t border-border" : ""}`}>
              {/* Row header: label → current → plu → delta */}
              <div className="flex items-center gap-2">
                {/* Label + sources + estimation indicator */}
                <div className="flex items-center gap-1 min-w-14">
                  <span className="text-sm font-semibold">{row.label}</span>
                  {row.sources && row.sources.length > 0 && <SourceTooltip sources={row.sources} />}
                  {hasEstimation && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            <TriangleAlert className="size-3 text-amber-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {row.label === "Etages"
                            ? "Nombre d'etages estime a partir de la hauteur (hauteur / 3m)"
                            : "Valeur basee sur un nombre d'etages estime (hauteur / 3m)"}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>

                {/* Current → PLU */}
                <div className="flex items-center gap-1.5 flex-1 justify-center">
                  <span className={`text-sm ${row.current === "—" ? "text-muted-foreground" : ""}`}>{row.current}</span>
                  <ArrowRight className="size-3 text-muted-foreground/50" />
                  <span
                    className={`text-sm font-semibold flex items-center gap-1 ${row.plu === "—" ? "text-muted-foreground" : "text-primary"}`}
                  >
                    {row.plu}
                    {row.isParentValue && row.parentLibelle && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <TriangleAlert className="size-3 text-amber-500" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Valeur issue de la zone parente {row.parentLibelle}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {row.confidence && (
                      <span
                        className={`size-1.5 rounded-full ${confidenceColor(row.confidence)}`}
                        title={row.confidence}
                      />
                    )}
                  </span>
                </div>

                {/* Delta badge */}
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold min-w-14 justify-center ${
                    row.isPositive
                      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20"
                      : row.isNegative
                        ? "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {row.diff}
                </span>
              </div>

              {/* Progress bar */}
              {row.progress !== null && (
                <div className="mt-1.5 h-1 rounded-full bg-primary/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${row.progress}%` }}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Collapsible sections */}
      <div className="rounded-lg border border-border overflow-hidden">
        <BuildingSection buildings={buildings} />
        <PluSection pluZone={plu_zone} />
      </div>
    </div>
  )
}
